import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import useApi from '../hooks/useApi';
import Header from '../components/Header';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import { ALL_SKILLS, POPULAR_SKILLS } from '../config/skills';

const JobsPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    type: 'all',
    experience: 'all',
    salaryMin: '',
    salaryMax: '',
    skills: [],
    postedWithin: 'all'
  });
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [showAllSkills, setShowAllSkills] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  
  // Check if user is a logged-in candidate
  const isCandidateLoggedIn = isAuthenticated && user?.userType === 'candidate';
  
  // Initialize search from URL params
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    const urlLocation = searchParams.get('location');
    
    if (urlSearch) setSearchQuery(urlSearch);
    if (urlLocation) setFilters(prev => ({ ...prev, location: urlLocation }));
  }, [searchParams]);
  
  // Fetch real jobs from API
  const { data: jobsData, loading: jobsLoading } = useApi('jobs');
  
  // API wraps response in {success: true, data: {jobs: [...]}}
  const allJobs = jobsData?.data?.jobs || jobsData?.jobs || [];
  
  // Extract unique values for filters
  const uniqueLocations = [...new Set(allJobs.map(job => job.location).filter(Boolean))];
  
  // Show popular skills or filtered skills based on search
  const displaySkills = skillSearchQuery
    ? ALL_SKILLS.filter(skill => skill.toLowerCase().includes(skillSearchQuery.toLowerCase())).slice(0, 20)
    : showAllSkills
    ? ALL_SKILLS.slice(0, 30)
    : POPULAR_SKILLS;
  
  // Filter jobs based on all criteria
  const jobs = allJobs.filter(job => {
    // Search filter
    const matchesSearch = !searchQuery || 
      job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (job.skills || []).some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Location filter
    const matchesLocation = !filters.location || 
      job.location?.toLowerCase().includes(filters.location.toLowerCase());
    
    // Job type filter
    const matchesType = filters.type === 'all' || 
      job.type?.toLowerCase() === filters.type.toLowerCase();
    
    // Experience level filter
    const matchesExperience = filters.experience === 'all' || 
      job.experience?.toLowerCase().includes(filters.experience.toLowerCase());
    
    // Salary filter
    const matchesSalary = (!filters.salaryMin || parseInt(job.salaryMin) >= parseInt(filters.salaryMin)) &&
                          (!filters.salaryMax || parseInt(job.salaryMax) <= parseInt(filters.salaryMax));
    
    // Skills filter
    const matchesSkills = filters.skills.length === 0 || 
      filters.skills.every(skill => (job.skills || []).some(s => s.toLowerCase() === skill.toLowerCase()));
    
    // Posted within filter
    const matchesPostedDate = filters.postedWithin === 'all' || (() => {
      const daysAgo = Math.floor((new Date() - new Date(job.createdAt)) / (1000 * 60 * 60 * 24));
      switch(filters.postedWithin) {
        case '24h': return daysAgo <= 1;
        case '7d': return daysAgo <= 7;
        case '30d': return daysAgo <= 30;
        default: return true;
      }
    })();
    
    return matchesSearch && matchesLocation && matchesType && 
           matchesExperience && matchesSalary && matchesSkills && matchesPostedDate;
  });
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const toggleSkillFilter = (skill) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };
  
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      location: '',
      type: 'all',
      experience: 'all',
      salaryMin: '',
      salaryMax: '',
      skills: [],
      postedWithin: 'all'
    });
  };
  
  const activeFiltersCount = Object.values(filters).filter(v => 
    v !== 'all' && v !== '' && (Array.isArray(v) ? v.length > 0 : true)
  ).length + (searchQuery ? 1 : 0);
  
  const handleJobClick = (jobId) => {
    if (!user) {
      navigate('/auth/candidate', { state: { from: `/candidate/jobs/${jobId}` } });
    } else {
      navigate(`/candidate/jobs/${jobId}`);
    }
  };
  
  const handleApplyNow = (e, jobId) => {
    e.stopPropagation();
    if (!user) {
      navigate('/auth/candidate', { state: { from: `/candidate/jobs/${jobId}`, action: 'apply' } });
    } else {
      navigate(`/candidate/jobs/${jobId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Show sidebar if candidate is logged in */}
      {isCandidateLoggedIn && <CandidateSidebar />}
      
      {/* Hide header if candidate is logged in */}
      {!isCandidateLoggedIn && <Header />}
      
      <main className={`${isCandidateLoggedIn ? 'ml-0 md:ml-24' : ''} max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
        {/* Search Bar */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <h1 className="text-3xl font-bold text-black mb-4">Find Your Dream Job</h1>
          <input
            type="text"
            placeholder="Search by job title, company, keywords, or skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <aside className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-black">Filters</h2>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-gray-600 hover:text-black"
                  >
                    Clear ({activeFiltersCount})
                  </button>
                )}
              </div>

              {/* Job Type */}
              <div className="mb-6">
                <h3 className="font-semibold text-black mb-2">Job Type</h3>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              {/* Experience Level */}
              <div className="mb-6">
                <h3 className="font-semibold text-black mb-2">Experience Level</h3>
                <select
                  value={filters.experience}
                  onChange={(e) => handleFilterChange('experience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Levels</option>
                  <option value="0-1">0-1 years</option>
                  <option value="1-3">1-3 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              {/* Location */}
              <div className="mb-6">
                <h3 className="font-semibold text-black mb-2">Location</h3>
                <input
                  type="text"
                  placeholder="City, state..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Salary Range */}
              <div className="mb-6">
                <h3 className="font-semibold text-black mb-2">Salary Range (LPA)</h3>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.salaryMin}
                    onChange={(e) => handleFilterChange('salaryMin', e.target.value)}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.salaryMax}
                    onChange={(e) => handleFilterChange('salaryMax', e.target.value)}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Posted Within */}
              <div className="mb-6">
                <h3 className="font-semibold text-black mb-2">Posted Within</h3>
                <select
                  value={filters.postedWithin}
                  onChange={(e) => handleFilterChange('postedWithin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">Any Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>

            </div>
          </aside>

          {/* Jobs List */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4">
              <p className="text-gray-600">
                Showing <span className="font-semibold text-black">{jobs.length}</span> of <span className="font-semibold">{allJobs.length}</span> {jobs.length === 1 ? 'job' : 'jobs'}
              </p>
            </div>

            {/* Jobs List Content */}
            {jobsLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No jobs available at the moment</p>
            <p className="text-gray-500 text-sm mt-2">Check back soon for new opportunities!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job, index) => {
              const companyInitials = job.companyName
                ? job.companyName.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase()
                : 'CO';
              
              const logoColors = [
                'bg-blue-100 text-blue-600',
                'bg-green-100 text-green-600',
                'bg-purple-100 text-purple-600',
                'bg-red-100 text-red-600',
                'bg-yellow-100 text-yellow-600',
                'bg-indigo-100 text-indigo-600'
              ];
              const logoColor = logoColors[index % logoColors.length];
              
              const daysAgo = Math.floor((new Date() - new Date(job.createdAt)) / (1000 * 60 * 60 * 24));
              
              return (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index, duration: 0.3 }}
                  onClick={() => handleJobClick(job.id)}
                  className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer hover:border-black"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className={`h-14 w-14 ${logoColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <span className="font-bold text-lg">{companyInitials}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-black mb-1">{job.title}</h3>
                          <p className="text-gray-600 mb-2">{job.companyName || 'Company'}</p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                            <span>📍 {job.location}</span>
                            <span>💼 {job.type}</span>
                            <span>💰 ₹{job.salaryMin}-{job.salaryMax} LPA</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(job.skills || []).slice(0, 5).map((skill, idx) => (
                              <span key={idx} className="bg-gray-100 text-gray-800 text-xs px-3 py-1 rounded-full">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      <span className="text-sm text-gray-500">
                        {daysAgo === 0 ? 'Posted today' : `Posted ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`}
                      </span>
                      <button
                        onClick={(e) => handleApplyNow(e, job.id)}
                        className="bg-black text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors"
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            </div>
          )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobsPage;
